import { addDoc, collection, doc, documentId, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, startAfter, updateDoc, where } from 'firebase/firestore'
import { auth, db } from '../firebase'
import { consumeEmergencyOtp } from './descopeEmergencyOtp'
import { buildEmergencyScope, canAccessEmergency } from '../data/turkeyProvinces'

export const EMERGENCY_API_URL = 'https://populi-destek-795950092325.europe-west3.run.app'
export const EMERGENCY_API_READY = true
const HISTORY_COLLECTION = 'emergencyCalls'

export function subscribeEmergencyHistory(onData, onError) {
  return onSnapshot(
    query(collection(db, HISTORY_COLLECTION), orderBy('createdAt', 'desc'), limit(100)),
    (snapshot) => onData(snapshot.docs.map((item) => ({ ...item.data(), id: item.id }))),
    onError,
  )
}

const RECIPIENT_PAGE_SIZE = 100

export async function loadEmergencyRecipients(requestId, cursor = null) {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(requestId || '')) throw new Error('Geçersiz çağrı kimliği.')
  const constraints = [where('requestId', '==', requestId), orderBy(documentId()), limit(RECIPIENT_PAGE_SIZE)]
  if (cursor) constraints.push(startAfter(cursor))
  const snapshot = await getDocs(query(collection(db, 'destek'), ...constraints))
  return {
    items: snapshot.docs.map((item) => ({ ...item.data(), id: item.id })),
    cursor: snapshot.docs.length === RECIPIENT_PAGE_SIZE ? snapshot.docs[snapshot.docs.length - 1] : null,
  }
}

function responseText(body) {
  if (!body) return 'Çağrı isteği servis tarafından kabul edildi.'
  try {
    const data = JSON.parse(body)
    if (typeof data === 'string') return data
    const text = data.text || data.message || data.response || data.answer || data.detail || data.data
    return typeof text === 'string' ? text : JSON.stringify(data)
  } catch {
    return body
  }
}

function safeApiResult(body) {
  try {
    const data = JSON.parse(body)
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null
    const result = {}
    if (Number.isFinite(data.matchedUsers)) result.matchedUsers = data.matchedUsers
    if (data.counts && typeof data.counts === 'object' && !Array.isArray(data.counts)) {
      result.counts = Object.fromEntries(Object.entries(data.counts).filter(([key, value]) => /^[a-z_]+$/.test(key) && Number.isFinite(value)))
    }
    return Object.keys(result).length ? result : null
  } catch {
    return null
  }
}

async function sendEmergencyRequest({ requestId, scope, provinceCodes, provinces }) {
  const formData = new FormData()
  formData.append('requestId', requestId)
  formData.append('scope', scope)
  formData.append('provinceCodes', JSON.stringify(provinceCodes))
  formData.append('provinces', JSON.stringify(provinces))
  let response
  try {
    response = await fetch(EMERGENCY_API_URL, {
      method: 'POST',
      body: formData,
    })
  } catch (cause) {
    throw Object.assign(new Error('Çağrı servisine ulaşılamadı. Yeniden başlatmadan önce çağrı geçmişini kontrol edin.'), { cause, outcome: 'unknown' })
  }

  const body = await response.text()
  const text = responseText(body)
  if (!response.ok) {
    throw Object.assign(new Error(text), { outcome: response.status >= 400 && response.status < 500 ? 'rejected' : 'unknown' })
  }
  return { text, apiResult: safeApiResult(body) }
}

// The transport must resolve only when API acceptance is confirmed. It should throw
// with outcome="rejected" for a definitive rejection; all ambiguous outcomes stay unknown.
export async function submitEmergencyCall({ scope, provinceCodes, currentUser, otpProof }, transport = sendEmergencyRequest) {
  const selection = buildEmergencyScope(scope, provinceCodes)
  if (!auth.currentUser || auth.currentUser.uid !== currentUser?.uid) throw new Error('Oturumunuzu yenileyin.')
  const profileSnapshot = await getDoc(doc(db, 'users', currentUser.uid))
  const profile = profileSnapshot.exists() ? profileSnapshot.data() : {}
  if (profile.archived === true || !canAccessEmergency({ ...profile, uid: currentUser.uid, isAnonymous: auth.currentUser.isAnonymous }, profile.status || 'active')) {
    throw new Error('Bu işlem için acil durum ekibi yetkisi gerekiyor.')
  }
  const firebaseIdToken = await auth.currentUser.getIdToken()
  const descopeSessionToken = consumeEmergencyOtp(otpProof, { uid: currentUser.uid, scope, provinceCodes })
  // Record the attempt before dispatch; an audit write failure must prevent dispatch.
  const historyRef = await addDoc(collection(db, HISTORY_COLLECTION), {
    ...selection,
    initiatedBy: currentUser.uid,
    initiatedByName: profile.nickname || auth.currentUser.email || '',
    initiatedByEmail: auth.currentUser.email || '',
    createdAt: serverTimestamp(),
    status: 'pending',
  })
  let status = 'accepted'
  let message = 'Çağrı isteği servis tarafından kabul edildi.'
  let apiResponseText = ''
  let apiResult = null
  try {
    const result = await transport({ ...selection, provinceCodes: selection.provinces.map((province) => province.code), requestId: historyRef.id, firebaseIdToken, descopeSessionToken })
    apiResponseText = result?.text || ''
    apiResult = result?.apiResult || null
    message = apiResponseText || message
  } catch (error) {
    status = error.outcome === 'rejected' ? 'rejected' : 'unknown'
    apiResponseText = error.message || ''
    message = status === 'rejected'
      ? 'Çağrı isteği servis tarafından reddedildi.'
      : 'Çağrının sonucu doğrulanamadı. Yeniden başlatmadan önce kontrol edin.'
    if (apiResponseText) message = apiResponseText
  }
  try {
    await updateDoc(historyRef, { status, apiResponseText, ...(apiResult ? { apiResult } : {}), updatedAt: serverTimestamp() })
  } catch {
    return { status, message: `${message} Geçmişteki durum güncellenemedi; kayıt ${historyRef.id}.`, apiResponseText, historyWarning: true }
  }
  return { status, message, apiResponseText }
}
